import { useEffect, useRef } from 'react';
import noop from 'lodash/noop';
import { useFragment, graphql } from 'react-relay';
import { useTrigger } from '@atlassian/post-office-context';
import { fg } from '@atlassian/jira-feature-gating';
import { useListNINConfigMigration } from '../../../services/list-nin-config-migration/useListNINConfigMigration.tsx';
import { usePaidListNINConfigMigration } from '../../../services/list-nin-config-migration/usePaidListNINConfigMigration.tsx';
import { useIssueSearchActions } from '../../../services/issue-search/selectors.tsx';
import type { MigrateListConfigClickListener_JiraView$key } from './__generated__/MigrateListConfigClickListener_JiraView.graphql';
import type { MigrateListConfigClickListener_JiraProject$key } from './__generated__/MigrateListConfigClickListener_JiraProject.graphql';

const MIGRATE_CONFIG_TRIGGER =
	'jira-nin-list-merge-modal-onboarding-explore-list-view-button-clicked-with-copy-list-settings';
const MIGRATE_CONFIG_TRIGGER_PAID_CURRENT_SPACE =
	'jira-nin-paid-list-merge-modal-onboarding-explore-list-view-button-clicked-migrate-current-space';
const MIGRATE_CONFIG_TRIGGER_PAID_ALL_SPACES =
	'jira-nin-paid-list-merge-modal-onboarding-explore-list-view-button-clicked-migrate-all-spaces';

type Props = {
	onChangeJqlAndGroupBy?: (params: { jql?: string; groupBy?: string }) => void;
	project: MigrateListConfigClickListener_JiraProject$key;
	view: MigrateListConfigClickListener_JiraView$key;
};
/**
 * Post Office Trigger Listener for the List NIN Merge
 * This component can be cleaned up when cleaning up jira_list_nin_merge & jira_paid_list_nin_merge FGs
 */
export const MigrateListConfigClickListener = ({ onChangeJqlAndGroupBy, project, view }: Props) => {
	const projectData = useFragment<MigrateListConfigClickListener_JiraProject$key>(
		graphql`
			fragment MigrateListConfigClickListener_JiraProject on JiraProject {
				projectId
				key
			}
		`,
		project,
	);

	const viewData = useFragment<MigrateListConfigClickListener_JiraView$key>(
		graphql`
			fragment MigrateListConfigClickListener_JiraView on JiraIssueSearchViewMetadata
			@argumentDefinitions(
				isSavedListViewTechnicalChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-technical-changes-enabled.relayprovider"
				}
			) {
				id
				savedViewId @include(if: $isSavedListViewTechnicalChangesEnabled)
			}
		`,
		view,
	);

	let viewId = viewData.id;
	if (fg('saved_list_view_technical_changes')) {
		if (!viewData.savedViewId) {
			throw new Error('Missing saved view ID');
		}
		viewId = viewData.savedViewId;
	}

	const { changeGroupedByFieldAndJql } = fg('jira_paid_list_nin_merge')
		? // eslint-disable-next-line  react-hooks/rules-of-hooks
			useIssueSearchActions()
		: {
				changeGroupedByFieldAndJql: noop,
			};

	const migrateListConfig = useListNINConfigMigration({
		projectId: projectData.projectId,
		projectKey: projectData.key,
		viewId,
		onChangeJqlAndGroupBy,
	});

	const { migrateCurrentProject, migrateAllProjects } = fg('jira_paid_list_nin_merge')
		? // eslint-disable-next-line  react-hooks/rules-of-hooks
			usePaidListNINConfigMigration({
				currentProjectId: projectData.projectId,
				currentProjectKey: projectData.key,
				onChangeJqlAndGroupBy,
				onMigrationCompleted: changeGroupedByFieldAndJql,
			})
		: {
				migrateCurrentProject: noop,
				migrateAllProjects: noop,
			};

	const hasClicked = useRef(false);

	const { isActive: isMigrateListConfigClicked } = useTrigger(MIGRATE_CONFIG_TRIGGER);
	const { isActive: isMigrateListConfigClickedPaidCurrentProject } = fg('jira_paid_list_nin_merge')
		? // eslint-disable-next-line  react-hooks/rules-of-hooks
			useTrigger(MIGRATE_CONFIG_TRIGGER_PAID_CURRENT_SPACE)
		: {
				isActive: false,
			};
	const { isActive: isMigrateListConfigClickedPaidAllProjects } = fg('jira_paid_list_nin_merge')
		? // eslint-disable-next-line  react-hooks/rules-of-hooks
			useTrigger(MIGRATE_CONFIG_TRIGGER_PAID_ALL_SPACES)
		: {
				isActive: false,
			};

	useEffect(() => {
		if (!hasClicked.current && isMigrateListConfigClicked) {
			migrateListConfig();
			hasClicked.current = true;
		}
	}, [isMigrateListConfigClicked, migrateListConfig]);

	useEffect(() => {
		if (
			!hasClicked.current &&
			isMigrateListConfigClickedPaidCurrentProject &&
			fg('jira_paid_list_nin_merge')
		) {
			migrateCurrentProject();
			hasClicked.current = true;
		}
	}, [isMigrateListConfigClickedPaidCurrentProject, migrateCurrentProject]);

	useEffect(() => {
		if (
			!hasClicked.current &&
			isMigrateListConfigClickedPaidAllProjects &&
			fg('jira_paid_list_nin_merge')
		) {
			migrateAllProjects();
			hasClicked.current = true;
		}
	}, [isMigrateListConfigClickedPaidAllProjects, migrateAllProjects]);

	return null;
};
